import {
  FormControl,
  FormControlLabel,
  Radio as MuiRadio,
  RadioGroup as MuiRadioGroup,
  type SxProps,
} from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { useThemeTokens } from '@providers/themeTokenProvider';
import type { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpText } from '../helpText/helpText';
import { useHelpTextTokens } from '../helpText/helpText.tokens';
import InfoDialog from '../infoDialog/infoDialog';
import { useRadioTokens } from './radio.tokens';

type RadioFunctionalProps = {
  value: string | number;
  onChange?: (event: SyntheticEvent<Element, Event>, checked: boolean) => void;
  disabled?: boolean;
};
type RadioVisualProps = {
  label: React.ReactNode;
  subLabel?: string;
  size?: 'medium' | 'small';
  testId?: string;
};

export const Radio = (props: RadioFunctionalProps & RadioVisualProps) => {
  const tokens = useThemeTokens(useRadioTokens());
  const { comp } = tokens;

  const fontSizes = {
    small: '14px',
    medium: '16px',
  };
  const lineHeights = {
    small: '20px',
    medium: '24px',
  };
  return (
    <>
      <FormControlLabel
        value={props.value}
        onChange={props.onChange}
        disabled={props.disabled}
        label={props.label}
        disableTypography
        sx={{
          fontFamily:
            comp.radio.fontFamily ||
            'Interphases Pro, "Causten Round", Helvetica, sans-serif',
          fontSize: fontSizes[props.size ?? 'medium'],
          lineHeight: lineHeights[props.size ?? 'medium'],
          fontWeight: 400,
          color: !props.disabled
            ? comp.radio.defaultColor
            : comp.radio.disabledColor,
        }}
        control={
          <MuiRadio
            data-testid={props.testId}
            size={props.size}
            inputProps={
              {
                'data-testid': `${props.testId ?? 'radio'}-input`,
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
            sx={{
              '&.MuiRadio-root': {
                color: comp.radio.defaultColor,
                '&.Mui-checked': { color: comp.radio.activeColor },
                '&.Mui-disabled': { color: comp.radio.disabledColor },
              },
            }}
          />
        }
      />
      {props.subLabel && (
        <Typography
          variant='bodyMedium'
          onClick={(e: any) => props.onChange && props.onChange(e, true)}
          sx={{
            color: comp.radio.subLabelColor,
            ml: 4,
            mt: -1,
            cursor: 'pointer',
            mb: 1,
          }}
        >
          {props.subLabel}
        </Typography>
      )}
    </>
  );
};

type RadioGroupProps = {
  label?: string;
  placeholder?: string;
  helpText?: string;
  error?: boolean;
  errorText?: string;
  success?: boolean;
  successText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  children?: React.ReactNode[] | React.ReactNode;
  value?: string | number;
  onChange?: any;
  onFocus?: any;
  infoTooltip?: string;
  testId?: string;
  required?: boolean;
};

export const RadioGroup = ({
  label,
  helpText,
  error = false,
  errorText,
  success = false,
  successText,
  fullWidth,
  disabled = false,
  sx,
  onChange,
  value,
  children,
  infoTooltip,
  testId,
  required,
}: RadioGroupProps) => {
  const tokens = useThemeTokens(useRadioTokens());
  const { comp: helpTextComp } = useThemeTokens(useHelpTextTokens());
  const { t } = useTranslation('components');
  const { comp } = tokens;
  // Create a key that changes when value changes from undefined to defined
  // so that the component can be re-rendered when one of the options is selected for the first time.
  const radioGroupKey =
    value !== undefined ? 'defined-value' : 'undefined-value';

  const shouldDisplayInDialog = helpTextComp.helpText.displayInDialog;
  const helpTextToShow =
    infoTooltip && !shouldDisplayInDialog ? infoTooltip : helpText;

  const helpTextFunc = (isError: boolean, isSuccess: boolean) => {
    if (isError) {
      return (
        (errorText ?? t('fieldErrors.default', { fieldName: label })) ||
        helpTextToShow
      );
    }

    if (isSuccess) {
      return successText || helpTextToShow;
    }

    return helpTextToShow;
  };

  return (
    <FormControl
      data-testid={testId ?? 'selection-tile'}
      sx={{ ...sx, width: fullWidth ? '100%' : 'default' }}
      disabled={disabled}
    >
      <Box display='flex' flexDirection='row' alignItems={'center'}>
        {label && (
          <Typography
            variant='labelSmall'
            sx={{
              mb: '4px',
              color: error ? comp.radio.errorColor : comp.radio.labelColor,
            }}
          >
            {label}
            {required && (
              <span style={{ color: comp.radio.requiredIndicatorColor }}>
                *
              </span>
            )}
            {infoTooltip && shouldDisplayInDialog && (
              <InfoDialog information={infoTooltip} />
            )}
          </Typography>
        )}
      </Box>
      <MuiRadioGroup
        row
        key={radioGroupKey}
        aria-labelledby='demo-row-radio-buttons-group-label'
        name='row-radio-buttons-group'
        value={value}
        onChange={onChange}
      >
        {children}
      </MuiRadioGroup>
      {helpTextFunc(error, success) && (
        <HelpText
          text={helpTextFunc(error, success)}
          tone={error ? 'error' : success ? 'success' : 'neutral'}
        />
      )}
    </FormControl>
  );
};
